// pmBoard — data layer for the PM Board (/pm-board), Sep 11, 2026.
//
// Greg: "a project manager board. mobile friendly. 7 day calendar at top, click
// a job and it provides easily clickable options for their duties while on
// site. they gather photos of job … a guide through day that they can check off
// … send a return pick up email to srs at end of day with list of returns and
// photo. allow jennifer, shannon to edit list of responsibilities and tasks …
// build out a to do / not do, allow jennifer to sort."
//
// READ PATH: `pm_board_jobs(from, to)` — a SECURITY DEFINER RPC. Ramiro's
// project_manager role has NO deals SELECT policy and must not get one (a
// role-wide policy exposes every financial column). The RPC returns a narrow,
// non-financial projection. Never add a `.from("deals")` read to this board.
//
// TEMPLATES (pm_task_templates) + RULES (pm_rules) are the editable guide —
// written only by the setup tier (admin / sales_director / dir-ops / finance).
// JOB STATE (pm_job_tasks, pm_job_photos) is per-deal and written by the
// working tier. The return-pickup log is written ONLY by the edge function.

import { supabase } from "./supabase";
import { fetchEdge, readEdgeJson } from "./edgeError";
import { heicToJpeg } from "./heicToJpeg";
import { compressImage } from "../components/canvas/MediaExtrasRow";
import { tagsMatchLabel } from "./companycam";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export type PmTaskKind = "check" | "photo" | "note" | "return_email";

export interface PmTemplate {
  id: string;
  section: string;
  label: string;
  kind: PmTaskKind;
  min_photos: number;
  required: boolean;
  hint: string | null;
  sort_order: number;
  is_active: boolean;
  updated_at: string;
}

export interface PmRule {
  id: string;
  kind: "do" | "dont";
  text: string;
  sort_order: number;
  is_active: boolean;
}

export interface PmJobTask {
  id: string;
  deal_id: string;
  template_id: string;
  done: boolean;
  done_at: string | null;
  done_by: string | null;
  note: string | null;
}

export interface PmJobPhoto {
  id: string;
  deal_id: string;
  template_id: string | null;
  document_id: string | null;
  storage_path: string;
  file_name: string | null;
  taken_by: string | null;
  created_at: string;
}

export interface PmReturnSend {
  id: string;
  deal_id: string;
  pickup_date: string | null;
  lines: Array<{ qty: number; item: string; note?: string }>;
  photo_paths: string[];
  sent_by: string | null;
  send_status: "sent" | "failed";
  error_message: string | null;
  sent_at: string;
}

/** The narrow deal projection the RPC returns. Money fields are deliberately
 *  absent — see the header. */
export interface PmJob {
  id: string;
  homeowner_name: string | null;
  homeowner_phone: string | null;
  property_street: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  build_scheduled_date: string | null;
  build_completed_date: string | null;
  stage: string | null;
  phase: string | null;
  owner_id: string | null;
  split_rm_id: string | null;
  claim_number: string | null;
  shingle_color: string | null;
  drip_edge_color: string | null;
  total_squares: number | string | null;
  build_notes: string | null;
  pre_inspection_data: Record<string, unknown> | null;
  pm_workbook: Record<string, unknown> | null;
  /** Oct 30, 2026 (scope gate): the carrier lines the homeowner kept, as the RM
   *  ticked them — [{n, area, description, quantity, unit}], no money. Null when
   *  there are none (or before the scope gate migration). */
  do_not_perform?: Array<{ n: string | null; area: string | null; description: string; quantity: number | null; unit: string | null }> | null;
}

/* ------------------------------------------------------------------ */
/* Dates (date-only strings, ET calendar day — never new Date(dateOnly)) */
/* ------------------------------------------------------------------ */

export function todayISO(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}
export function addDaysISO(iso: string, n: number): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  d.setDate(d.getDate() + n);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
export function fmtDay(iso: string, opts: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" }): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", opts);
}
export function fmtStamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/* ------------------------------------------------------------------ */
/* Reads                                                               */
/* ------------------------------------------------------------------ */

export async function fetchPmJobs(fromISO: string, toISO: string): Promise<PmJob[]> {
  const { data, error } = await supabase.rpc("pm_board_jobs", { p_from: fromISO, p_to: toISO });
  if (error) throw new Error(error.message);
  return (Array.isArray(data) ? data : []) as PmJob[];
}

export async function fetchTemplates(includeInactive = false): Promise<PmTemplate[]> {
  let q = supabase.from("pm_task_templates")
    .select("id,section,label,kind,min_photos,required,hint,sort_order,is_active,updated_at")
    .order("sort_order", { ascending: true }).order("created_at", { ascending: true });
  if (!includeInactive) q = q.eq("is_active", true);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as PmTemplate[];
}

export async function fetchRules(includeInactive = false): Promise<PmRule[]> {
  let q = supabase.from("pm_rules").select("id,kind,text,sort_order,is_active")
    .order("sort_order", { ascending: true }).order("created_at", { ascending: true });
  if (!includeInactive) q = q.eq("is_active", true);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as PmRule[];
}

export async function fetchJobState(dealIds: string[]): Promise<{
  tasks: Record<string, PmJobTask[]>;
  photos: Record<string, PmJobPhoto[]>;
  /** Tags of each job's tagged CompanyCam photos — a photo tagged with a
   *  guide item's name completes that item (crews shoot in CompanyCam). */
  ccTags: Record<string, string[][]>;
  returns: Record<string, PmReturnSend>;
}> {
  const out = { tasks: {} as Record<string, PmJobTask[]>, photos: {} as Record<string, PmJobPhoto[]>, ccTags: {} as Record<string, string[][]>, returns: {} as Record<string, PmReturnSend> };
  if (dealIds.length === 0) return out;
  const [t, p, r, cc] = await Promise.all([
    supabase.from("pm_job_tasks").select("id,deal_id,template_id,done,done_at,done_by,note").in("deal_id", dealIds),
    supabase.from("pm_job_photos").select("id,deal_id,template_id,document_id,storage_path,file_name,taken_by,created_at").in("deal_id", dealIds).order("created_at", { ascending: true }),
    supabase.from("pm_return_pickup_emails").select("id,deal_id,pickup_date,lines,photo_paths,sent_by,send_status,error_message,sent_at").in("deal_id", dealIds).order("sent_at", { ascending: false }),
    supabase.from("companycam_photos").select("deal_id,tags").in("deal_id", dealIds).eq("status", "active").neq("tags", "{}").limit(5000),
  ]);
  if (t.error) throw new Error(t.error.message);
  if (p.error) throw new Error(p.error.message);
  if (r.error) throw new Error(r.error.message);
  for (const row of (t.data ?? []) as PmJobTask[]) (out.tasks[row.deal_id] ||= []).push(row);
  for (const row of (p.data ?? []) as PmJobPhoto[]) (out.photos[row.deal_id] ||= []).push(row);
  for (const row of (r.data ?? []) as PmReturnSend[]) if (!out.returns[row.deal_id]) out.returns[row.deal_id] = row; // newest first
  // CompanyCam tags are a bonus signal — a failed read leaves items to the
  // in-app photos / checkbox rather than breaking the board.
  if (cc.error) console.warn("companycam tags read failed:", cc.error.message);
  for (const row of (cc.data ?? []) as Array<{ deal_id: string; tags: string[] }>) (out.ccTags[row.deal_id] ||= []).push(row.tags);
  return out;
}

/* ------------------------------------------------------------------ */
/* Job state writes                                                    */
/* ------------------------------------------------------------------ */

export async function setTaskDone(dealId: string, templateId: string, done: boolean, byMemberId: string | null, note?: string | null): Promise<void> {
  const payload: Record<string, unknown> = {
    deal_id: dealId, template_id: templateId, done,
    done_at: done ? new Date().toISOString() : null,
    done_by: done ? byMemberId : null,
  };
  if (note !== undefined) payload.note = note;
  const { error } = await supabase.from("pm_job_tasks").upsert(payload, { onConflict: "deal_id,template_id" });
  if (error) throw new Error(error.message);
}

export async function setTaskNote(dealId: string, templateId: string, note: string): Promise<void> {
  const { error } = await supabase.from("pm_job_tasks")
    .upsert({ deal_id: dealId, template_id: templateId, note }, { onConflict: "deal_id,template_id" });
  if (error) throw new Error(error.message);
}

/** Upload one photo for a guide item: HEIC→JPEG, compressed to ~1280px, stored
 *  under the deal's folder, mirrored into deal_documents (file_type pm_photo)
 *  so it shows on the deal's Documents tab, then linked in pm_job_photos. */
export async function uploadJobPhoto(input: {
  dealId: string; templateId: string | null; templateLabel: string; file: File; memberId: string | null;
}): Promise<PmJobPhoto> {
  const jpeg = await heicToJpeg(input.file);
  let blob: Blob = jpeg;
  try { blob = await compressImage(jpeg, 1600, 0.82); } catch { /* fail open — upload the original */ }
  const safeBase = (input.templateLabel || "photo").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "photo";
  const fileName = `${safeBase}-${Date.now()}.jpg`;
  const path = `${input.dealId}/pm-photos/${fileName}`;
  const { error: upErr } = await supabase.storage.from("deal-documents")
    .upload(path, blob, { contentType: "image/jpeg" });
  if (upErr) throw new Error(`Upload failed: ${upErr.message}`);

  let documentId: string | null = null;
  const { data: doc, error: docErr } = await supabase.from("deal_documents").insert({
    deal_id: input.dealId,
    file_name: fileName,
    file_type: "pm_photo",
    storage_path: path,
    file_size: blob.size,
    mime_type: "image/jpeg",
    uploaded_by: input.memberId,
    notes: input.templateLabel,
  }).select("id").maybeSingle();
  if (docErr) console.warn("[pmBoard] deal_documents mirror failed:", docErr.message);
  else documentId = doc?.id ?? null;

  const { data: row, error } = await supabase.from("pm_job_photos").insert({
    deal_id: input.dealId,
    template_id: input.templateId,
    document_id: documentId,
    storage_path: path,
    file_name: fileName,
    taken_by: input.memberId,
  }).select("id,deal_id,template_id,document_id,storage_path,file_name,taken_by,created_at").single();
  if (error) throw new Error(error.message);
  return row as PmJobPhoto;
}

export async function deleteJobPhoto(photo: PmJobPhoto): Promise<void> {
  const { error } = await supabase.from("pm_job_photos").delete().eq("id", photo.id);
  if (error) throw new Error(error.message);
  // Best-effort: the mirrored document + storage object.
  if (photo.document_id) void supabase.from("deal_documents").delete().eq("id", photo.document_id);
  void supabase.storage.from("deal-documents").remove([photo.storage_path]);
}

export async function signedPhotoUrl(path: string, seconds = 3600): Promise<string | null> {
  const { data, error } = await supabase.storage.from("deal-documents").createSignedUrl(path, seconds);
  if (error) return null;
  return data?.signedUrl ?? null;
}

/* ------------------------------------------------------------------ */
/* Setup writes (templates + rules) — RLS limits these to the setup tier */
/* ------------------------------------------------------------------ */

export type PmTemplateInput = Pick<PmTemplate, "section" | "label" | "kind" | "min_photos" | "required" | "hint">;

export async function saveTemplate(id: string | null, input: PmTemplateInput, byMemberId: string | null, sortOrder?: number): Promise<PmTemplate> {
  const payload: Record<string, unknown> = { ...input, updated_by: byMemberId };
  if (sortOrder !== undefined) payload.sort_order = sortOrder;
  const sel = "id,section,label,kind,min_photos,required,hint,sort_order,is_active,updated_at";
  if (id) {
    const { data, error } = await supabase.from("pm_task_templates").update(payload).eq("id", id).select(sel).single();
    if (error) throw new Error(error.message);
    return data as PmTemplate;
  }
  const { data, error } = await supabase.from("pm_task_templates").insert({ ...payload, created_by: byMemberId }).select(sel).single();
  if (error) throw new Error(error.message);
  return data as PmTemplate;
}

export async function setTemplateActive(id: string, active: boolean, byMemberId: string | null): Promise<void> {
  const { error } = await supabase.from("pm_task_templates").update({ is_active: active, updated_by: byMemberId }).eq("id", id);
  if (error) throw new Error(error.message);
}

/** Persist a whole ordering in one go — sort_order = index × 10. */
export async function saveTemplateOrder(ids: string[], byMemberId: string | null): Promise<void> {
  await Promise.all(ids.map((id, i) =>
    supabase.from("pm_task_templates").update({ sort_order: (i + 1) * 10, updated_by: byMemberId }).eq("id", id)
      .then(({ error }) => { if (error) throw new Error(error.message); }),
  ));
}

export async function saveRule(id: string | null, input: { kind: "do" | "dont"; text: string }, byMemberId: string | null, sortOrder?: number): Promise<PmRule> {
  const payload: Record<string, unknown> = { ...input, updated_by: byMemberId };
  if (sortOrder !== undefined) payload.sort_order = sortOrder;
  const sel = "id,kind,text,sort_order,is_active";
  if (id) {
    const { data, error } = await supabase.from("pm_rules").update(payload).eq("id", id).select(sel).single();
    if (error) throw new Error(error.message);
    return data as PmRule;
  }
  const { data, error } = await supabase.from("pm_rules").insert({ ...payload, created_by: byMemberId }).select(sel).single();
  if (error) throw new Error(error.message);
  return data as PmRule;
}

export async function setRuleActive(id: string, active: boolean, byMemberId: string | null): Promise<void> {
  const { error } = await supabase.from("pm_rules").update({ is_active: active, updated_by: byMemberId }).eq("id", id);
  if (error) throw new Error(error.message);
}

export async function saveRuleOrder(ids: string[], byMemberId: string | null): Promise<void> {
  await Promise.all(ids.map((id, i) =>
    supabase.from("pm_rules").update({ sort_order: (i + 1) * 10, updated_by: byMemberId }).eq("id", id)
      .then(({ error }) => { if (error) throw new Error(error.message); }),
  ));
}

/* ------------------------------------------------------------------ */
/* Return pickup email                                                 */
/* ------------------------------------------------------------------ */

export interface ReturnLine { item: string; qty: number; note?: string }

/** The standard SRS lines a PM can send back — labels mirror the PM sheet's
 *  MATERIAL_ROWS (PMBoardTab.tsx). Free-text lines are allowed too. */
export const RETURN_ITEMS: string[] = [
  "Bundles OC Duration",
  "Bundles OC ProEdge",
  "Bundles OC Starter",
  "Rolls Rhino Shield",
  "Rolls Ice and Water",
  "Sticks Vent Sure",
  "Pipe Jacks w/ Rain Collars",
  "Sheets OSB",
  "Box Coil Nail Top Notch",
  "Bucket Cap Nail",
  "Tubes NP-1 Black",
  "Sticks Gutter Apron",
  "Sticks Drip Edge",
  "Box Step Flashing",
  "Cans Paint",
];

export interface ReturnSendResult { log_id: string | null; sent_at: string; to: string; cc: string[]; photo_count: number; attach_errors: string[] }

export async function sendReturnPickup(input: {
  dealId: string; pickupDate: string; notes: string; lines: ReturnLine[]; photoPaths: string[];
  /** CompanyCam photos (companycam_photos.id) for this job to attach. */
  companycamPhotoIds?: string[];
}): Promise<ReturnSendResult> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) throw new Error("You've been signed out — sign back in and try again.");

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/srs-return-pickup`;
  const res = await fetchEdge(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      deal_id: input.dealId,
      pickup_date: input.pickupDate,
      notes: input.notes,
      lines: input.lines,
      photo_paths: input.photoPaths,
      companycam_photo_ids: input.companycamPhotoIds ?? [],
    }),
  });
  const body = await readEdgeJson<Record<string, unknown>>(res);
  if (!res.ok || !body?.ok) {
    const code = (body?.error as string) || "";
    const detail = (body?.detail as string) || (body?.message as string) || "";
    if (code === "empty_return") throw new Error("Add at least one item going back before sending.");
    if (code === "forbidden") throw new Error("Your account isn't on the active roster, so it can't send to SRS.");
    if (code === "unauthorized") throw new Error("You've been signed out — sign back in and try again.");
    if (code === "send_failed") throw new Error(`The email didn't go out: ${detail || "the mail provider rejected it"}.`);
    throw new Error(detail || code || `Send failed (HTTP ${res.status}).`);
  }
  return {
    log_id: (body.log_id as string) ?? null,
    sent_at: (body.sent_at as string) ?? new Date().toISOString(),
    to: (body.to as string) ?? "",
    cc: (body.cc as string[]) ?? [],
    photo_count: Number(body.photo_count) || 0,
    attach_errors: (body.attach_errors as string[]) ?? [],
  };
}

/* ------------------------------------------------------------------ */
/* Progress helpers (pure)                                             */
/* ------------------------------------------------------------------ */

export function photoCountFor(photos: PmJobPhoto[] | undefined, templateId: string): number {
  return (photos ?? []).filter((p) => p.template_id === templateId).length;
}

/** Is this guide item complete for this job? Photo items complete as soon as
 *  ANY photo is in the section (Greg, Sep 11: no photo count — "just as long
 *  as some photos are in that section"; min_photos is kept in the schema but
 *  no longer read); return_email items complete once a pickup email has gone
 *  out; everything else follows the checkbox. */
export function itemDone(t: PmTemplate, task: PmJobTask | undefined, photoCount: number, returnSent: boolean): boolean {
  if (t.kind === "photo") return photoCount >= 1 || !!task?.done;
  if (t.kind === "return_email") return returnSent || !!task?.done;
  return !!task?.done;
}

/** CompanyCam photos (by their tag lists) tagged with this guide item's name. */
export function ccCountFor(ccTags: string[][] | undefined, label: string): number {
  return (ccTags ?? []).filter((tags) => tagsMatchLabel(tags, label)).length;
}

export function jobProgress(templates: PmTemplate[], tasks: PmJobTask[] | undefined, photos: PmJobPhoto[] | undefined, returnSent: boolean, ccTags?: string[][]): { done: number; total: number } {
  const byTpl: Record<string, PmJobTask> = {};
  for (const t of tasks ?? []) byTpl[t.template_id] = t;
  let done = 0, total = 0;
  for (const t of templates) {
    if (!t.required) continue;
    total += 1;
    if (itemDone(t, byTpl[t.id], photoCountFor(photos, t.id) + ccCountFor(ccTags, t.label), returnSent)) done += 1;
  }
  return { done, total };
}
